import { useState } from "react";
import axios, { AxiosError } from "axios";
import { message } from "antd";
import { leaseService } from "../../../../../../configs/constants";

export type ReceiptStatus = "approved" | "rejected";

export interface ApprovePaymentReceiptPayload {
  leaseId?: string;
  bookingId?: string;
  paymentReceiptId?: string;
  approvalStatus: ReceiptStatus;
  approvalType?: string;
  reason?: string;
  pdcId?: string;
  subPdcId?: string;
  isReceipt?: boolean;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

export const useApprovePaymentReceipt = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<ApiResponse<unknown> | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const approvePaymentReceipt = async (payload: ApprovePaymentReceiptPayload): Promise<ApiResponse<unknown>> => {
    const { leaseId, paymentReceiptId, approvalStatus, approvalType, reason, pdcId, subPdcId, isReceipt } = payload;

    setLoading(true);
    setError(null);

    try {
      const pathSegments = [approvalType];
      if (leaseId) pathSegments.push(leaseId);
      if (pdcId) pathSegments.push(pdcId);
      if (subPdcId) pathSegments.push(subPdcId);
      if (paymentReceiptId) pathSegments.push(paymentReceiptId);

      const basePath = isReceipt ? "receipt" : "booking";
      const url = `${leaseService}/${basePath}/${pathSegments.join("/")}`;

      const response = await axios.patch<ApiResponse<unknown>>(url, {
        approvalStatus,
        ...(reason && { reason: reason }),
      });
      setData(response.data);
      return response.data;
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>;

      const formattedError: ApiError = {
        message: axiosError.response?.data?.message || axiosError.message || "Something went wrong",
        errors: axiosError.response?.data?.errors,
      };
      message.error(formattedError.message);
      setError(formattedError);
      throw formattedError;
    } finally {
      setLoading(false);
    }
  };

  return {
    approvePaymentReceipt,
    loading,
    data,
    error,
  };
};
